import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ArrayHelper, FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  Answer,
  DataType,
  GroupResponse,
  ICoding,
  IContained,
  IFilter,
  IGroup,
  INPUT_TYPE,
  IObservationDefinition,
  IObservationResume,
  IPatientUser,
  IQuestionnaire,
  IQuestionQuestionnaire,
  IWidgetPatientParameter,
  PreferenceContext,
  QuestionnaireScoring,
  QuestionResponse,
  SPECIFIC_USE,
} from "@comunicare/comunicare-interfaces";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { QuestionnaireResponse } from "src/app/models/questionnaireResponse.model";
import { ObservationsService } from "src/app/providers/observations.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { QuestionnairesService } from "src/app/providers/questionnaires.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { PDF_TYPE_CONTEXT, PdfTypeChoiceComponent } from "../pdf-type-choice/pdf-type-choice.component";
import { QRDetailsDataSource } from "./QR-details-datasource";

@Pipe({ name: "questionAnswerStr" })
export class QuestionAnswerStrPipe implements PipeTransform {
  transform(reference: string, showAllQuestions: boolean, page: number, groupsResponse: GroupResponse[], pageNbr?: number): string {
    let question: QuestionResponse;
    if (!showAllQuestions || pageNbr) {
      const p = pageNbr || pageNbr === 0 ? pageNbr : page;
      question = groupsResponse?.[p]?.question?.find((q) => q.linkId === reference);
    } else {
      question = groupsResponse?.[0]?.question?.find((q) => q.linkId === reference);
    }
    const answers: Answer[] = question?.answer;
    let display = "";
    answers?.forEach((a, i) => {
      display += a.valueCoding?.display ?? a.valueCoding?.code;
      if (i < answers.length - 1) {
        if ((question.inputType !== INPUT_TYPE.NUMBER && question.inputType !== INPUT_TYPE.DECIMAL) || (i > 0 && i % 2 !== 0))
          display += ", ";
        else display += " ";
      }
    });
    return display;
  }
}
@Pipe({ name: "questionAnswerCode" })
export class QuestionAnswerCodePipe implements PipeTransform {
  transform(reference: string, showAllQuestions: boolean, page: number, groupsResponse: GroupResponse[], pageNbr?: number): string {
    let question: QuestionResponse;
    if (!showAllQuestions || pageNbr) {
      const p = pageNbr || pageNbr === 0 ? pageNbr : page;
      question = groupsResponse?.[p]?.question?.find((q) => q.linkId === reference);
    } else {
      question = groupsResponse?.[0]?.question?.find((q) => q.linkId === reference);
    }
    const answers: Answer[] = question?.answer;
    if (answers?.length) {
      return answers[0].valueCoding.code;
    } else {
      return reference;
    }
  }
}

@Pipe({ name: "valueSetChoices" })
export class ValueSetChoicesPipe implements PipeTransform {
  transform(reference: string, valueSets: IContained[]): ICoding[] {
    const contained = valueSets?.find((c) => c.resourceType === "ValueSet" && c.idSet === reference);
    return contained?.compose.include[0].concept ?? [];
  }
}

@Pipe({ name: "valueSetChoice" })
export class ValueSetChoicePipe implements PipeTransform {
  transform(code: string, valueSets: ICoding[]): ICoding {
    return valueSets?.find((c) => c.code === code);
  }
}

@Component({
  selector: "app-qr-details",
  templateUrl: "./qr-details.component.html",
  styleUrls: ["./qr-details.component.scss"],
})
export class QRDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  public viewType = "questionnaire";
  public withObservations = false;
  public linkedObservationsDef: IObservationDefinition[];
  public linkedObservations: IObservationResume[];
  public INPUT_TYPE = INPUT_TYPE;
  public nbSection = 1;
  public sections: { [pageNbr: number]: unknown[] } = {};
  public selectedSection: unknown[] = [];
  public page = 0;
  @ViewChild(MatTable) table: MatTable<IQuestionQuestionnaire>;
  @ViewChild(MatSort) sort: MatSort;
  public displayedColumns: string[] = ["name", "answer"];
  public dataSource: QRDetailsDataSource;
  public patientId: string;
  public editElementCode: string;
  public questionnaire: IQuestionnaire;
  public questionnaireResponse: QuestionnaireResponse;
  public groups: IGroup[];
  public groupsResponse: GroupResponse[];
  public showAllQuestions = false;
  public isQuiz = false;

  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";
  public loading = true;

  public mergedCategory: QuestionnaireScoring[][];
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  public constructor(
    private preferenceService: PreferenceService,
    private questionnaireService: QuestionnairesService,
    private observationService: ObservationsService,
    private responsiveDialog: ResponsiveDialogService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      QR: QuestionnaireResponse;
      scoring: QuestionnaireScoring[];
      pu?: IPatientUser;
    }
  ) {
    this.patientId = this.data.QR.subject.reference;
  }

  ngOnInit(): void {
    this.questionnaireResponse = this.data.QR;
    this.loadPreferences();
    this.groupsResponse = this.questionnaireResponse?.group?.group?.length
      ? Tools.deepCopy(this.questionnaireResponse?.group?.group)
      : Tools.deepCopy([this.questionnaireResponse?.group]);
    this.dataSource = new QRDetailsDataSource(this.questionnaireResponse, this.groupsResponse);
    this.mergedCategory = this.getMergedScoringByCategory(this.data.scoring);
  }

  ngAfterViewInit(): void {
    this.initPaginatorAndSort();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initPaginatorAndSort(): void {
    this.dataSource.sort = this.sort;
    this.table.dataSource = this.dataSource;
  }

  private loadDataSource(questions: IQuestionQuestionnaire[]): void {
    this.dataSource.loadData(questions, this.page);
    this.loading = false;
  }

  private async init(): Promise<void> {
    try {
      this.questionnaire = await this.questionnaireService.getQuestionnaire(
        this.data.QR.ref,
        this.data.QR.version,
        false,
        true,
        this.data.QR.questionnairePublicationDate
      );
      this.downloadLinkedObservationsDefs();
      this.downloadLinkedObservations();
      this.groups = this.questionnaire?.group?.group?.length
        ? Tools.deepCopy(this.questionnaire?.group?.group)
        : Tools.deepCopy([this.questionnaire?.group]);
      this.nbSection = this.groups.length;
      this.onShowAll(true);
      if (this.questionnaire.specificUse === SPECIFIC_USE.QUIZ) {
        this.isQuiz = true;
      }
      this.loading = false;
    } catch (err) {
      FileLogger.error("QRDetailsComponent", "Error while loading questionnaire: ", err);
    }
  }

  private downloadLinkedObservationsDefs(): void {
    const loincs = this.questionnaire?.observationLoincs?.map((o) => o.loinc);
    if (loincs?.length) {
      this.observationService
        .listDef(null, loincs)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (o: IObservationDefinition[]) => {
            this.linkedObservationsDef = o.filter(ArrayHelper.onlyUniqueLoincObsDef);
          },
          (err) => {
            FileLogger.error("QRDetailsComponent", "Error while downloading observations definitions", err, "none");
            this.linkedObservationsDef = [];
          }
        );
    }
  }

  private async downloadLinkedObservations(): Promise<void> {
    const questionnaireResponseRef = this.questionnaireResponse?.identifier.value;
    if (questionnaireResponseRef) {
      const fullObs = await this.observationService.getObservationsByQR(questionnaireResponseRef).catch((err) => {
        FileLogger.error("QRDetailsComponent", "Error while downloading observations", err, "none");
        return [];
      });
      this.linkedObservations = [];

      fullObs.forEach((obs) =>
        obs.component.forEach((obsComponent) => {
          if (obsComponent?.code?.coding && obsComponent.code.coding.length) {
            // prepare all availableObservations
            this.linkedObservations.push({
              // problem
              reference: obsComponent.code.coding[0].code,
              // we will need the parent's code to find the right definition:
              parentReference: obs.code.coding[0].code,
              display: obsComponent.code.coding[0].display,
              date: obs.issued,
              value: obsComponent.valueQuantity.value,
              unit: obsComponent.valueQuantity.unit,
              pictures: obsComponent.valuePictures,
              device: obs.device,
            });
          }
        })
      );
    }
  }

  public onShowAll(skipSave?: boolean): void {
    if (!skipSave) {
      this.updatePreference();
    }
    if (this.showAllQuestions) {
      this.groups[0].question = this.groups.reduce((acc, el) => {
        acc.push(...el.question);
        return acc;
      }, []);
      this.groupsResponse[0].question = this.groupsResponse.reduce((acc, el) => {
        acc.push(...el.question);
        return acc;
      }, []);
      this.loadDataSource(this.groups?.[0]?.question);
    } else {
      this.groupsResponse = this.questionnaireResponse?.group?.group?.length
        ? Tools.deepCopy(this.questionnaireResponse?.group?.group)
        : Tools.deepCopy([this.questionnaireResponse?.group]);
      this.groups = this.questionnaire?.group?.group?.length
        ? Tools.deepCopy(this.questionnaire?.group?.group)
        : Tools.deepCopy([this.questionnaire?.group]);
      this.loadDataSource(this.groups?.[this.page]?.question);
    }
  }

  public getGroupTitle(): string {
    return this.groups?.[this.page]?.title || null;
  }

  public changePage(sign: "-" | "+"): void {
    switch (sign) {
      case "-":
        if (!this.isFirstSection()) {
          this.page--;
          this.selectedSection = this.sections[this.page];
          this.loadDataSource(this.groups?.[this.page]?.question);
        }
        break;
      case "+":
        if (!this.isLastSection()) {
          this.page++;
          this.selectedSection = this.sections[this.page];
          this.loadDataSource(this.groups?.[this.page]?.question);
        }
        break;
      default:
        break;
    }
  }

  public isLastSection(): boolean {
    return this.page + 1 === this.nbSection;
  }

  public isFirstSection(): boolean {
    return this.page + 1 === 1;
  }

  public get isNoneData(): boolean {
    return this.dataSource?.data?.length === 0;
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
  }

  public clearFilter(): void {
    this.globalSearchValue = "";
    this.dataSource.clearFilter();
  }

  /**
   * Preferences
   */
  private updatePreference(): void {
    this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_QR_DETAILS,
        parameters: {
          filters: this.dataSource.getAllFilters(),
          allQresponse: this.showAllQuestions,
        } as IWidgetPatientParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private loadPreferences() {
    this.preferenceService
      .list(PreferenceContext.PATIENT_QR_DETAILS)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: IWidgetPatientParameter) => {
        if (parameters) {
          // Apply saved filters
          parameters.filters.forEach((filter: IFilter) => {
            this.applyFilter(filter);
          });
          this.showAllQuestions = parameters?.allQresponse;
        }
        this.init();
      });
  }

  private getMergedScoringByCategory(scorings: QuestionnaireScoring[]): QuestionnaireScoring[][] {
    const allCategories = this.uniq(scorings.map((s) => s.identifier?.value));
    const o = {};
    allCategories.forEach((cat) => (o[cat] = []));
    scorings.forEach((s) => {
      o[s.identifier?.value].push(s);
    });
    return Object.keys(o).reduce((acc, el) => {
      acc.push(o[el]);
      return acc;
    }, [] as QuestionnaireScoring[][]);
  }

  private uniq(a: string[]) {
    return Array.from(new Set(a));
  }

  public onExportPDF(): void {
    this.responsiveDialog.open(
      PdfTypeChoiceComponent,
      {
        data: {
          context: PDF_TYPE_CONTEXT.QUESTIONNAIRE,
          pu: this.data.pu,
          questionnaire: this.questionnaire,
          groupsResponse: this.groupsResponse,
          mergedCategory: this.mergedCategory,
          questionnaireResponse: this.questionnaireResponse,
        },
        disableClose: true,
      },
      {},
      {
        minWidth: "auto",
        width: "auto",
        maxWidth: "auto",

        minHeight: "auto",
        height: "auto",
        maxHeight: "auto",
      }
    );
  }
}
